import { useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as pcApi from "@/lib/api/portfolio-collections";
import { isWithinLastNYears } from "@/lib/date-utils";
import { FEATURE_FLAGS } from "@/lib/feature-flags";
import { useFeatureFlag } from "@/lib/hooks/use-feature-flags";
import { useGetInstrumentMetadata } from "@/lib/hooks/use-portfolio-collections";
import { useT } from "@/lib/i18n/use-t";
import {
  AVAILABLE_DATASETS,
  type DatasetType,
  type InstrumentMetadata,
  type InstrumentSearchResult,
} from "@/lib/services/data-api-types";
import { T } from "./instrument-search-dialog.i18n";

// Server action calls replaced with fetch()

const PAGE_SIZE = 25;

// Max results limits for providers without server-side pagination (match the
// upstream-client hardcoded limits — see searchEodhd and searchTwelve).
const MAX_RESULTS_LIMITS: Partial<Record<DatasetType, number>> = {
  eodhd: 50,
  twelve: 30,
};

export type InstrumentSelectResult = {
  instrument: InstrumentSearchResult;
  dataset: DatasetType;
  metadata: InstrumentMetadata | null;
  error?: string;
};

type InstrumentSearchDialogProps = {
  open: boolean;
  isPatchMode?: boolean; // To distinguish between "Search instrument" and "Patch instrument" modes
  linkedInstrument?: { ticker: string; dataset: string } | null; // Current linked instrument to disable in patch mode
  onClose: () => void;
  onSelect: (result: InstrumentSelectResult) => void;
  disableCustomInstruments?: boolean;
  disableCombinedInstruments?: boolean;
};

// Datasets whose upstream API supports real page/pageSize params. For every
// other dataset the server returns the full capped list in one shot and the UI
// slices locally — so the page number must NOT vary the React Query key (a
// changing key would bypass the cache and refire the upstream call per click).
const SERVER_PAGED_DATASETS: DatasetType[] = [
  "morningstar_funds",
  "morningstar_etf",
  "morningstar_category",
];

// Fetcher for instrument search
const searchInstruments = (searchTerm: string, dataSource: DatasetType, page: number) =>
  pcApi.searchInstruments({
    searchTerm,
    datasets: [dataSource],
    page,
    pageSize: PAGE_SIZE,
  });

export function InstrumentSearchDialog({
  open,
  isPatchMode = false,
  linkedInstrument,
  onClose,
  onSelect,
  disableCustomInstruments = false,
  disableCombinedInstruments = false,
}: InstrumentSearchDialogProps) {
  const t = useT(T);

  const getMetadata = useGetInstrumentMetadata();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearch, setActiveSearch] = useState(""); // The search term we're actually searching for
  const [dataSource, setDataSource] = useState<DatasetType>("morningstar_funds");
  const [currentPage, setCurrentPage] = useState(1);
  const isPending = getMetadata.isPending;
  const [metadataError, setMetadataError] = useState<string | null>(null);

  // Data-vendor feature flags — datasets are only offered when the tenant has the
  // vendor enabled (mirrors the server gate on searchInstruments). The shared
  // custom/combined datasets are unaffected.
  const morningstarData = useFeatureFlag(FEATURE_FLAGS.MORNINGSTAR_DATA);
  const morningstarIndex = useFeatureFlag(FEATURE_FLAGS.MORNINGSTAR_INDEX);
  const twelveData = useFeatureFlag(FEATURE_FLAGS.TWELVE_DATA);
  const eodhdData = useFeatureFlag(FEATURE_FLAGS.EODHD);

  // Treat a flag as enabled while its query is still in flight, so a vendor dataset
  // isn't transiently hidden (and the default reset below isn't tripped) before the
  // real value arrives.
  const flagOn = (f: { enabled: boolean; isLoading: boolean }) => f.enabled || f.isLoading;
  const flagsLoading =
    morningstarData.isLoading ||
    morningstarIndex.isLoading ||
    twelveData.isLoading ||
    eodhdData.isLoading;

  const FILTERED_DATASETS = useMemo(
    () =>
      AVAILABLE_DATASETS.filter((ds) => {
        if (disableCustomInstruments && ds.id === "custom") return false;
        if (disableCombinedInstruments && ds.id === "combined") return false;
        // Indexes have their own vendor flag, separate from the shared
        // morningstar_data flag that gates funds/etf/category.
        if (ds.id === "morningstar_index") return flagOn(morningstarIndex);
        if (ds.id.startsWith("morningstar_") && !flagOn(morningstarData)) return false;
        if (ds.id === "twelve" && !flagOn(twelveData)) return false;
        if (ds.id === "eodhd" && !flagOn(eodhdData)) return false;
        return true;
      }),
    [
      disableCustomInstruments,
      disableCombinedInstruments,
      morningstarData.enabled,
      morningstarData.isLoading,
      morningstarIndex.enabled,
      morningstarIndex.isLoading,
      twelveData.enabled,
      twelveData.isLoading,
      eodhdData.enabled,
      eodhdData.isLoading,
    ],
  );

  // If the selected dataset gets filtered out (vendor disabled), fall back to the
  // first still-available one — but only once the flags have resolved, so the
  // default isn't switched away while queries are still loading.
  useEffect(() => {
    if (
      !flagsLoading &&
      FILTERED_DATASETS.length > 0 &&
      !FILTERED_DATASETS.some((d) => d.id === dataSource)
    ) {
      setDataSource(FILTERED_DATASETS[0].id as DatasetType);
    }
  }, [flagsLoading, FILTERED_DATASETS, dataSource]);

  const enabled = !!activeSearch && activeSearch.trim().length >= 2;
  const serverPaged = SERVER_PAGED_DATASETS.includes(dataSource);
  const queryPage = serverPaged ? currentPage : 1;

  const { data, error, isLoading } = useQuery({
    queryKey: ["instrument-search", activeSearch, dataSource, queryPage],
    queryFn: () => searchInstruments(activeSearch, dataSource, queryPage),
    enabled,
    staleTime: 60_000, // 1 minute
    refetchOnWindowFocus: false,
  });

  // When the server already paged (morningstar), render results as-is.
  // When the provider can't page (eodhd/twelve/combined/custom), slice locally.
  const results = data?.results ?? [];
  const total = data?.total ?? 0;
  const paged = data?.paged ?? false;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const displayedResults = paged
    ? results
    : results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = useCallback(() => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      return;
    }
    setActiveSearch(searchTerm);
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSelect = (instrument: InstrumentSearchResult) => {
    setMetadataError(null);

    getMetadata.mutate(
      { ticker: instrument.ticker, dataset: dataSource, exchange: instrument.exchange },
      {
        onSuccess: (instrumentData: any) => {
          const metadata = {
            name: instrumentData.name || instrument.name,
            isin: instrumentData.isin || instrument.isin || "",
            currency: instrumentData.currency || "",
            firstdate: instrumentData.firstdate || "",
            lastdate: instrumentData.lastdate || "",
          } as InstrumentMetadata;

          if (isPatchMode && isWithinLastNYears(metadata.firstdate || "", 10)) {
            toast.error(t.insufficientHistoricalDataTitle, {
              description: t.insufficientHistoricalDataDescription,
            });
          } else {
            onSelect({ instrument, dataset: dataSource, metadata });
            onClose();
            setSearchTerm("");
            setActiveSearch("");
            setCurrentPage(1);
            setMetadataError(null);
          }
        },
        onError: (err) => {
          const errorMsg = err.message || t.failedToFetchMetadata;
          setMetadataError(`${errorMsg} ${t.notLinkedWarning}`);

          onSelect({
            instrument,
            dataset: dataSource,
            metadata: null,
            error: errorMsg,
          });

          onClose();
          setSearchTerm("");
          setActiveSearch("");
          setCurrentPage(1);
          setMetadataError(null);
        },
      },
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const formatResultCount = (): string => {
    if (total <= PAGE_SIZE) {
      return t.showingAllResults(total.toString());
    }
    const from = (currentPage - 1) * PAGE_SIZE + 1;
    const to = Math.min(currentPage * PAGE_SIZE, total);
    return t.showingResults({ from: from.toString(), to: to.toString(), total: total.toString() });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isPatchMode ? t.patchInstrumentTitle : t.searchInstrumentDatabase}
          </DialogTitle>
          <DialogDescription>
            {isPatchMode ? t.patchInstrumentDescription : t.searchDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col min-h-[400px]">
          {/* Data Source Selection */}
          <div className="space-y-2">
            <Label>{t.dataSource}</Label>
            <Select
              value={dataSource}
              onValueChange={(value: DatasetType) => {
                setDataSource(value);
                // Reset pagination when switching data source
                setCurrentPage(1);
                setActiveSearch("");
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTERED_DATASETS.map((ds) => (
                  <SelectItem key={ds.id} value={ds.id}>
                    {ds.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search Input */}
          <div className="space-y-2">
            <Label htmlFor="search">{t.searchByName}</Label>
            <div className="flex gap-2">
              <SearchInput
                id="search"
                className="flex-1"
                value={searchTerm}
                onValueChange={setSearchTerm}
                onKeyPress={handleKeyPress}
                placeholder={t.searchPlaceholder}
                disabled={isLoading}
              />
              <Button
                data-dialog-guard-ignore
                onClick={handleSearch}
                disabled={searchTerm.trim().length < 2}
                loading={isLoading}
              >
                {t.search}
              </Button>
            </div>
          </div>

          {/* Error Message */}
          {(error || metadataError) && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {metadataError || error?.message || t.failedToSearch}
            </div>
          )}

          {/* Results */}
          {total > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">{formatResultCount()}</div>
              {totalPages > 1 && (
                <div className="text-sm text-muted-foreground">
                  {t.pageOf(currentPage.toString(), totalPages.toString())}
                </div>
              )}
            </div>
          )}

          {/* Max results warning for APIs without server-side pagination */}
          {(() => {
            const maxLimit = MAX_RESULTS_LIMITS[dataSource];
            if (maxLimit && total >= maxLimit) {
              return (
                <div className="text-sm rounded-md border border-warning/40 bg-warning/10 p-2">
                  {t.maxResultsWarning(maxLimit.toString())}
                </div>
              );
            }
            return null;
          })()}

          {/* Loading State */}
          {isLoading && (
            <div className="flex-1 flex items-center justify-center text-center text-muted-foreground p-8">
              <div>
                <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin opacity-50" />
                <p>{t.searching}</p>
              </div>
            </div>
          )}

          {!isLoading &&
            displayedResults.length > 0 &&
            (() => {
              // Show ISIN column for datasets that provide ISIN
              const showIsin = ["morningstar_funds", "morningstar_etf", "eodhd"].includes(
                dataSource,
              );
              const showExchange = displayedResults.some(
                (r: any) => typeof r.exchange === "string" && r.exchange.length > 0,
              );
              return (
                <div className="flex-1 overflow-auto border rounded-md">
                  <Table variant="data" surface={false}>
                    <TableHeader className="sticky top-0 z-10">
                      <TableRow>
                        <TableHead>{t.name}</TableHead>
                        {showIsin && <TableHead>{t.isin}</TableHead>}
                        <TableHead>{t.currency}</TableHead>
                        {showExchange && <TableHead>{t.exchange}</TableHead>}
                        <TableHead className="w-[100px]">{t.action}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayedResults.map((instrument: any) => (
                        <TableRow key={`${instrument.ticker}|${instrument.exchange ?? ""}`}>
                          <TableCell className="font-medium">{instrument.name}</TableCell>
                          {showIsin && (
                            <TableCell className="font-mono text-sm">
                              {instrument.isin || "—"}
                            </TableCell>
                          )}
                          <TableCell>{instrument.currency || "—"}</TableCell>
                          {showExchange && (
                            <TableCell className="text-sm text-muted-foreground">
                              {instrument.exchange || "—"}
                            </TableCell>
                          )}
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSelect(instrument)}
                              disabled={
                                isPending ||
                                (isPatchMode &&
                                  linkedInstrument?.ticker === instrument.ticker &&
                                  linkedInstrument?.dataset === dataSource)
                              }
                            >
                              {t.select}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              );
            })()}

          {/* Pagination Controls */}
          {totalPages > 1 && displayedResults.length > 0 && (
            <div className="flex items-center justify-center gap-2 pt-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
              >
                {t.previous}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
              >
                {t.next}
              </Button>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && displayedResults.length === 0 && activeSearch && !error && (
            <div className="flex-1 flex items-center justify-center text-center text-muted-foreground p-8">
              <div>
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>
                  {t.noInstrumentsFound} &quot;{activeSearch}&quot;
                </p>
                <p className="text-sm mt-2">{t.tryDifferentSearch}</p>
              </div>
            </div>
          )}

          {/* Initial State */}
          {!isLoading && displayedResults.length === 0 && !activeSearch && !error && (
            <div className="flex-1 flex items-center justify-center text-center text-muted-foreground p-8">
              <div>
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t.enterNameToSearch}</p>
              </div>
            </div>
          )}
        </div>

        {/* Metadata fetch loading overlay */}
        {isPending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/50 rounded-md">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t.fetchingMetadata}
          </div>
        )}

        <div className="flex justify-end">
          <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
        </div>
      </DialogContent>
    </Dialog>
  );
}
